import type { PaymentMethodSummary, PosLookupResponse } from '@aeris/contracts'

/**
 * O cliente do Aeris Edge — o servidor da loja (F042).
 *
 * ## Por que um arquivo separado de `api.ts`
 *
 * Os contratos são diferentes, e isso está escrito no código do Edge: o
 * cabeçalho de `apps/edge/src/routes/till.ts` diz que ele **deliberadamente**
 * não reproduz as formas de `/api/v1`. Um cliente só, que tentasse esconder a
 * diferença, teria de traduzir payloads nos dois sentidos em silêncio — e a
 * primeira vez que os dois divergissem, o balcão descobriria por um campo
 * vazio no meio de uma venda.
 *
 * Então a tradução acontece aqui, à vista, e produz **as formas da nuvem**: a
 * tela do PDV continua falando um vocabulário só. O que o Edge não tem não é
 * inventado — é null, e a tela sabe o que fazer com null porque a nuvem também
 * manda null quando não sabe.
 *
 * ## Por que tudo passa por `/edge`
 *
 * Dentro do aplicativo instalado, o proxy em Rust encaminha `/edge/` para o
 * servidor da loja, e a página é servida da mesma origem — então não há CORS,
 * não há conteúdo misto, e o navegador não precisa saber que existe outro
 * servidor. Num navegador comum o nginx responde 503 `edge_not_configured`, que
 * é a resposta honesta: aquele navegador não fala com o Edge.
 */

/** O envelope de erro do Edge é o mesmo da nuvem. */
export class EdgeError extends Error {
  readonly code: string
  readonly status: number

  constructor(status: number, code: string, message: string) {
    super(message)
    this.name = 'EdgeError'
    this.status = status
    this.code = code
  }
}

/**
 * O Edge não respondeu — não é o mesmo que ter respondido "não".
 *
 * A distinção decide o modo do balcão: 401 é "o Edge está vivo e este terminal
 * não está pareado", que se resolve numa tela; erro de rede é "não há Edge
 * aqui", que se resolve olhando o computador. Tratar os dois igual faria o PDV
 * oferecer o modo offline numa máquina que não tem Edge nenhum.
 */
export class EdgeUnreachableError extends Error {
  constructor() {
    super('O Aeris Edge desta loja não respondeu.')
    this.name = 'EdgeUnreachableError'
  }
}

const request = async <T>(
  path: string,
  init: { method?: string; body?: unknown; signal?: AbortSignal } = {}
): Promise<T> => {
  let response: Response

  try {
    response = await fetch(`/edge/v1${path}`, {
      method: init.method ?? 'GET',
      ...(init.signal === undefined ? {} : { signal: init.signal }),
      ...(init.body === undefined
        ? {}
        : {
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify(init.body),
          }),
    })
  } catch {
    // Só falha de rede chega aqui. Qualquer status HTTP — inclusive 401 e 503
    // — é uma resposta, e resposta significa que existe alguém do outro lado.
    throw new EdgeUnreachableError()
  }

  if (!response.ok) {
    const body: unknown = await response.json().catch(() => null)
    const envelope =
      typeof body === 'object' && body !== null
        ? (body as Record<string, unknown>)
        : {}

    throw new EdgeError(
      response.status,
      typeof envelope.code === 'string' ? envelope.code : 'edge_error',
      typeof envelope.message === 'string'
        ? envelope.message
        : 'O Aeris Edge recusou a operação.'
    )
  }

  return (await response.json()) as T
}

/* --- As formas do Edge, como ele as devolve ------------------------------- */

type EdgeMatch = {
  variantId: string
  productId: string
  description: string
  sku: string
  gtin: string | null
  unit: string
  exact: boolean
  quantityOnHand: string
  tracksStock: boolean
  /** Ausentes num Edge anterior à F143. */
  supplierReference?: string | null
  location?: string | null
}

type EdgeTier = {
  id: string
  variantId: string
  minQuantity: string
  unitPrice: string
  validFrom: string | null
  validTo: string | null
  status: 'active' | 'archived'
}

type EdgeLookup = {
  matches: EdgeMatch[]
  pricing: {
    variantId: string
    /** F176. Ausente num Edge anterior a ela. */
    fallbackVariantId?: string | null
    priceListId: string | null
    tiers: EdgeTier[]
  } | null
}

export type EdgeSession = {
  id: string
  terminalId: string
  status: 'open' | 'closed'
  openingFloat: string
  openedAt: string
  expectedAmount: string
}

export type EdgeSyncStatus = {
  pending: number
  /**
   * Vendas que a nuvem recusou ao subir (F197). O Edge já contava; a tela não
   * mostrava, e a venda recusada sumia calada.
   */
  refused?: number
  /** Há quanto tempo sem subir, e se ainda pode vender (F029). */
  offline?: {
    hoursOffline: number | null
    maxHours: number
    canSell: boolean
    lastSyncedAt: string | null
  }
  lastSyncedAt: string | null
  /**
   * Se a loja autorizou vender abaixo de zero (F103).
   *
   * Ausente num Edge anterior a este campo, e ausente é NÃO — o mesmo lado
   * conservador que o `default 0` da migração local escolhe.
   */
  allowNegativeStock?: boolean
}

/* --- A tradução ----------------------------------------------------------- */

/**
 * O que o Edge sabe, dito no vocabulário da nuvem.
 *
 * Os campos ausentes são **null**, e não valores inventados:
 *
 *   * `externalCode` — o código do GDOOR não desce no snapshot. Quem procura
 *     peça pelo número da planilha antiga vai ter de usar o SKU no offline, e
 *     isso é um limite escrito, não um campo mentindo que não existe código.
 *   * `thumbUrl` — a foto mora no S3, atrás da nuvem. Offline não há foto, e um
 *     `<img>` apontando para o vazio seria pior do que nenhum.
 *   * `unitPrice` — o Edge só precifica quando a resposta é inequívoca, igual à
 *     nuvem; a lista de candidatos não carrega preço.
 *
 * `onHand` respeita a F028: quando a loja não conta o produto, é null, e null
 * **não** é zero. Sem isso, os 5309 produtos importados da Elite Digital — que
 * ninguém contou — apareceriam todos como "sem estoque" no balcão offline.
 */
const toPosMatch = (
  match: EdgeMatch
): PosLookupResponse['matches'][number] => ({
  variantId: match.variantId,
  productId: match.productId,
  description: match.description,
  sku: match.sku,
  externalCode: null,
  gtin: match.gtin,
  unit: match.unit,
  thumbUrl: null,
  exact: match.exact,
  unitPrice: null,
  onHand: match.tracksStock ? match.quantityOnHand : null,
  tracksStock: match.tracksStock,
  // F141 no balcão offline (F143): a localização é a desta filial.
  supplierReference: match.supplierReference ?? null,
  location: match.location ?? null,
})

export const edgeLookup = async (
  term: string,
  signal?: AbortSignal
): Promise<PosLookupResponse> => {
  const answer = await request<EdgeLookup>(
    `/pos/lookup?term=${encodeURIComponent(term)}`,
    { ...(signal === undefined ? {} : { signal }) }
  )

  return {
    matches: answer.matches.map(toPosMatch),
    pricing:
      answer.pricing === null
        ? null
        : {
            variantId: answer.pricing.variantId,
            // Um Edge anterior à F176 não manda: resolve como antes.
            fallbackVariantId: answer.pricing.fallbackVariantId ?? null,
            /*
             * O Edge guarda o id da tabela, e não a tabela (F197).
             *
             * Era `null`, e `resolvePrice` recusa sem tabela ("a conta não tem
             * uma tabela de preço padrão"): **nenhuma peça tinha preço sem
             * internet**. A tabela do snapshot é a padrão da filial — é a
             * única que desce —, então ela entra como a padrão, ativa, sem
             * validade; o nome é o que a tela pode dizer sem inventar.
             */
            priceList:
              answer.pricing.priceListId === null
                ? null
                : {
                    id: answer.pricing.priceListId,
                    code: 'EDGE',
                    name: 'Tabela do balcão',
                    currency: 'BRL',
                    isDefault: true,
                    validFrom: null,
                    validTo: null,
                    status: 'active',
                  },
            tiers: answer.pricing.tiers,
          },
  }
}

export const edgeCurrentSession = async (
  terminalId: string,
  signal?: AbortSignal
): Promise<EdgeSession | null> => {
  const answer = await request<{ session: EdgeSession | null }>(
    `/cash/current?terminalId=${encodeURIComponent(terminalId)}`,
    { ...(signal === undefined ? {} : { signal }) }
  )

  return answer.session
}

export const edgeOpenSession = async (input: {
  terminalId: string
  openingFloat: string
  actorUserId: string | null
}): Promise<EdgeSession> => {
  const answer = await request<{ session: EdgeSession }>('/cash/open', {
    method: 'POST',
    body: input,
  })

  return answer.session
}

export type EdgeCheckoutInput = {
  cashSessionId: string
  customerPartyId?: string
  actorUserId: string | null
  /** O vendedor escolhido no PDV, para a comissão (F142). */
  salespersonUserId: string | null
  lines: readonly {
    variantId: string
    description: string
    unit: string
    quantity: string
    unitPrice: string
    priceBasis: string
    priceListId: string | null
  }[]
  /**
   * O desconto da nota, em REAIS e já derivado (F100).
   *
   * O Edge aceita este campo desde sempre (`apps/edge/src/routes/till.ts`) e
   * não conhece percentual — nem precisa: uma venda de balcão aqui é fechada
   * no mesmo instante e nunca reaberta, então não há intenção para lembrar.
   * Quem deriva é o `pdv.tsx`, com a mesma `computeTotals` que pintou o total
   * na tela.
   */
  discountAmount?: string
  payments: readonly { paymentMethodId: string; amount: string }[]
}

/**
 * Uma venda no Edge, dita como a nuvem diria.
 *
 * O que muda de verdade, e a tela precisa mostrar: **não há número de
 * documento**. A numeração é da nuvem, e um Edge que inventasse a sua produziria
 * dois documentos com o mesmo número no dia em que dois balcões vendessem
 * offline ao mesmo tempo. A venda tem a referência local do Edge, e ganha
 * número quando sobe.
 */
export type EdgeSale = {
  /** A referência do Edge, a que vai no recibo provisório. */
  reference: string
  documentId: string
  total: string
  subtotal: string
  changeDue: string
  cashSessionId: string
}

/*
 * F197: lia `sale.id` e `sale.total`, que o Edge nunca mandou — a venda dava
 * certo no Edge e a tela "Venda concluída" quebrava lendo `undefined`. Os nomes
 * são os de `CheckoutResult` em `apps/edge/src/store/till.ts`.
 */
export const edgeCheckout = async (
  input: EdgeCheckoutInput
): Promise<EdgeSale> => {
  const answer = await request<{
    sale: {
      documentId: string
      localReference: string
      totalAmount: string
      subtotalAmount: string
      changeDue: string
      cashSessionId: string
    }
  }>('/pos/checkout', { method: 'POST', body: input })

  return {
    reference: answer.sale.localReference,
    documentId: answer.sale.documentId,
    total: answer.sale.totalAmount,
    subtotal: answer.sale.subtotalAmount,
    changeDue: answer.sale.changeDue,
    cashSessionId: answer.sale.cashSessionId,
  }
}

export type EdgePaymentMethod = {
  id: string
  code: string
  name: string
  kind: string
  displayOrder: number
}

/**
 * As formas de pagamento do balcão desconectado (F197), do snapshot — sem o
 * vale. Antes o PDV as lia da nuvem, e um caixa aberto já sem internet não
 * tinha nenhuma.
 */
export const edgePaymentMethods = async (
  signal?: AbortSignal
): Promise<EdgePaymentMethod[]> =>
  (
    await request<{ methods: EdgePaymentMethod[] }>('/pos/payment-methods', {
      ...(signal === undefined ? {} : { signal }),
    })
  ).methods

/**
 * A forma do Edge no vocabulário da nuvem, para o diálogo de pagamento não
 * precisar saber de onde veio. O que o snapshot não traz é o neutro: troco só
 * no dinheiro, uma parcela, nada a receber depois.
 */
export const toPaymentMethodSummary = (
  method: EdgePaymentMethod
): PaymentMethodSummary => ({
  id: method.id,
  code: method.code,
  name: method.name,
  kind: method.kind as PaymentMethodSummary['kind'],
  settlesImmediately: method.kind === 'cash',
  settlementDays: 0,
  feePercent: '0',
  allowsChange: method.kind === 'cash',
  maxInstalments: 1,
  generatesReceivable: false,
  receivableDueDays: 0,
  status: 'active',
  displayOrder: method.displayOrder,
  version: 0,
  updatedAt: new Date(0).toISOString(),
})

/**
 * Parear este caixa com o Edge da loja (F197).
 *
 * Online, de propósito: o Edge pergunta à nuvem quem é o dono do cookie que o
 * aplicativo repassa, e só quem opera o PDV pareia. O token que volta é
 * entregue ao shell, que o guarda e o põe em cada chamada ao Edge — a página
 * nunca o guarda.
 */
export const edgePair = async (terminalId: string): Promise<string> => {
  const answer = await request<{ token: string }>('/pair', {
    method: 'POST',
    body: { terminalId },
  })
  return answer.token
}

/** A loja e a filial que este Edge atende. */
export const edgeIdentity = async (
  signal?: AbortSignal
): Promise<{ tenantName: string; branchName: string } | null> =>
  (
    await request<{
      identity: { tenantName: string; branchName: string } | null
    }>('/identity', { ...(signal === undefined ? {} : { signal }) })
  ).identity

/** Quantas vendas ainda não subiram. É o número do aviso permanente do balcão. */
export const edgeSyncStatus = async (
  signal?: AbortSignal
): Promise<EdgeSyncStatus> =>
  await request<EdgeSyncStatus>('/sync/status', {
    ...(signal === undefined ? {} : { signal }),
  })
